/*
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */
import React, { useState, useContext, useEffect, useCallback, useRef } from 'react';
import { message, Modal } from 'antd';
import styled from 'styled-components';
import {
  MediaType,
  onUserJoinedEvent,
  onUserLeaveEvent,
  PlayerEvent,
  AutoPlayFailedEvent,
} from '@byteplus/rtc';

import { ControlBar, AutoPlayModal } from '../../modules';

import { Context } from '../../context';

import RTCComponent from '../../sdk/rtc-component';
import { RTCClient } from '../../app-interfaces';
import { streamOptions } from './constant';
import config from '../../config';
import MediaPlayer from '../../components/MediaPlayer';
import { removeLoginInfo } from '../../utils';

const Container = styled.div`
  width: 100%;
  height: 100%;
  background: #0a0a0a;
  position: relative;
  overflow: hidden;
`;

const RemoteGrid = styled.div<{ participantCount: number }>`
  width: 100%;
  height: 100%;
  display: grid;
  gap: 12px;
  padding: 12px;
  
  ${({ participantCount }) => {
    if (participantCount === 1) {
      // Single remote: full stage
      return `
        grid-template-columns: 1fr;
        grid-template-rows: 1fr;
      `;
    } else if (participantCount === 2) {
      // Two remotes: side by side
      return `
        grid-template-columns: 1fr 1fr;
        grid-template-rows: 1fr;
      `;
    } else if (participantCount === 3) {
      // Three remotes: one large + two stacked (FaceTime style)
      return `
        grid-template-columns: 2fr 1fr;
        grid-template-rows: 1fr 1fr;
        & > *:first-child {
          grid-row: 1 / 3;
        }
      `;
    } else {
      // 4+ remotes: balanced grid
      const cols = Math.ceil(Math.sqrt(participantCount));
      return `
        grid-template-columns: repeat(${cols}, 1fr);
        grid-template-rows: repeat(${Math.ceil(participantCount / cols)}, 1fr);
      `;
    }
  }}
`;

const RemoteTile = styled.div`
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: transform 250ms ease, box-shadow 250ms ease;
  background: #000;
`;

const LocalOnlyOverlay = styled.div`
  position: absolute;
  bottom: 7%;
  left: 0;
  right: 0;
  text-align: center;
  pointer-events: none;
  z-index: 2;
`;

const WaitingCaption = styled.div`
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.3px;
`;

type CornerPosition = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
type LocalMode = 'full' | 'pip';
type PipSize = 'sm' | 'md' | 'lg';

// PiP dimensions per size. Width drives layout; height = width * 9/16.
// Keep these in sync with PIP_WIDTHS_PX used by drag clamping below.
const PIP_SIZE_RULES: Record<PipSize, string> = {
  sm: `
    width: clamp(96px, 12vw, 160px);
    aspect-ratio: 16 / 9;
    @media (max-width: 768px) {
      width: clamp(80px, 20vw, 96px);
    }
  `,
  md: `
    width: clamp(160px, 18vw, 240px);
    aspect-ratio: 16 / 9;
    @media (max-width: 768px) {
      width: clamp(120px, 28vw, 160px);
    }
  `,
  lg: `
    width: clamp(240px, 26vw, 360px);
    aspect-ratio: 16 / 9;
    @media (max-width: 768px) {
      width: clamp(180px, 40vw, 240px);
    }
  `,
};

// Approx widths (px) for drag clamping — matches the upper clamp() bound at common viewports.
const PIP_WIDTHS_PX: Record<PipSize, number> = {
  sm: 160,
  md: 240,
  lg: 360,
};

const SIZE_CYCLE: Record<PipSize, PipSize> = {
  sm: 'md',
  md: 'lg',
  lg: 'sm',
};

const SIZE_LABEL: Record<PipSize, string> = {
  sm: 'S',
  md: 'M',
  lg: 'L',
};

const LocalContainer = styled.div<{
  mode: LocalMode;
  corner: CornerPosition;
  size: PipSize;
  isDragging: boolean;
  dragPosition?: { x: number; y: number };
}>`
  position: absolute;
  z-index: ${({ mode }) => (mode === 'pip' ? 1000 : 1)};
  overflow: hidden;
  background: #000;
  border: ${({ mode }) =>
    mode === 'pip'
      ? '2px solid rgba(255, 255, 255, 0.12)'
      : '1px solid rgba(255, 255, 255, 0.08)'};
  box-shadow: ${({ mode, isDragging }) => {
    if (mode === 'full') return '0 12px 48px rgba(0, 0, 0, 0.6)';
    return isDragging
      ? '0 20px 60px rgba(0, 0, 0, 0.8)'
      : '0 8px 24px rgba(0, 0, 0, 0.6)';
  }};
  border-radius: ${({ mode }) => (mode === 'pip' ? '14px' : '16px')};
  cursor: ${({ mode, isDragging }) =>
    mode === 'pip' ? (isDragging ? 'grabbing' : 'grab') : 'default'};
  touch-action: none;
  user-select: none;
  transform: scale(${({ isDragging }) => (isDragging ? 1.02 : 1)});
  transition: ${({ isDragging }) =>
    isDragging
      ? 'box-shadow 150ms ease, transform 150ms ease'
      : 'top 250ms ease, left 250ms ease, right 250ms ease, bottom 250ms ease, width 250ms ease, height 250ms ease, transform 250ms ease, box-shadow 250ms ease, border-radius 250ms ease'};

  ${({ mode, corner, size, isDragging, dragPosition }) => {
    if (mode === 'full') {
      return `
        top: 7.5%;
        left: 5%;
        width: 90%;
        height: 85%;
      `;
    }

    const sizeRules = PIP_SIZE_RULES[size];

    if (isDragging && dragPosition) {
      return `
        left: ${dragPosition.x}px;
        top: ${dragPosition.y}px;
        ${sizeRules}
      `;
    }

    switch (corner) {
      case 'top-right':
        return `top: 16px; right: 16px; ${sizeRules}`;
      case 'top-left':
        return `top: 16px; left: 16px; ${sizeRules}`;
      case 'bottom-right':
        return `bottom: 116px; right: 16px; ${sizeRules}`;
      case 'bottom-left':
        return `bottom: 116px; left: 16px; ${sizeRules}`;
      default:
        return `top: 16px; right: 16px; ${sizeRules}`;
    }
  }}
`;

const Meeting: React.FC<Record<string, unknown>> = () => {
  const { roomId, userId, setJoin, setJoinFailReason } = useContext(Context);
  const [isMicOn, setMicOn] = useState<boolean>(true);
  const [isVideoOn, setVideoOn] = useState<boolean>(true);
  const rtc = useRef<RTCClient>();
  const [autoPlayFailUser, setAutoPlayFailUser] = useState<string[]>([]);
  const playStatus = useRef<{ [key: string]: { audio: boolean; video: boolean } }>({});
  const autoPlayFailUserdRef = useRef<string[]>([]);

  const [remoteStreams, setRemoteStreams] = useState<{
    [key: string]: {
      playerComp: React.ReactNode;
    };
  }>({});

  const [idlePromptOpen, setIdlePromptOpen] = useState(false);
  const [idleSecondsLeft, setIdleSecondsLeft] = useState(0);
  const idleIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const idleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleCountdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [pipCorner, setPipCorner] = useState<CornerPosition>('top-right');
  const [pipSize, setPipSize] = useState<PipSize>('md');
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState<{ x: number; y: number } | undefined>();
  const dragStartRef = useRef<{ x: number; y: number; offsetX: number; offsetY: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handlePipPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
    
    const rect = e.currentTarget.getBoundingClientRect();
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
    };
    
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePipPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging || !dragStartRef.current || !containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const newX = e.clientX - dragStartRef.current.offsetX;
    const newY = e.clientY - dragStartRef.current.offsetY;

    const w = PIP_WIDTHS_PX[pipSize];
    const h = Math.round((w * 9) / 16);
    const clampedX = Math.max(0, Math.min(newX, containerRect.width - w));
    const clampedY = Math.max(0, Math.min(newY, containerRect.height - h));

    setDragPosition({ x: clampedX, y: clampedY });
  };

  const handlePipPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging || !containerRef.current) return;

    setIsDragging(false);
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);

    if (!dragPosition) {
      return;
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    const w = PIP_WIDTHS_PX[pipSize];
    const h = Math.round((w * 9) / 16);
    const centerX = dragPosition.x + w / 2;
    const centerY = dragPosition.y + h / 2;

    const isLeft = centerX < containerRect.width / 2;
    const isTop = centerY < (containerRect.height - 100) / 2;
    
    let newCorner: CornerPosition;
    if (isTop && !isLeft) {
      newCorner = 'top-right';
    } else if (isTop && isLeft) {
      newCorner = 'top-left';
    } else if (!isTop && !isLeft) {
      newCorner = 'bottom-right';
    } else {
      newCorner = 'bottom-left';
    }
    
    setPipCorner(newCorner);
    setDragPosition(undefined);
    dragStartRef.current = null;
  };

  const leaveRoom = useCallback(
    (refresh: boolean) => {
      if (!rtc.current) return;

      // off the event
      rtc.current.removeEventListener();

      rtc.current.leave();
      if (!refresh) {
        setJoin(false);
        removeLoginInfo();
      }

      setAutoPlayFailUser([]);
      setJoinFailReason('');
    },
    [rtc, setJoin]
  );

  /**
   * @brief call leaveRoom function when the browser window closes or refreshes
   */
  const leaveFunc = () => {
    leaveRoom(true);
    sessionStorage.setItem('store', JSON.stringify({ test: new Date().toString() }));
  };
  useEffect(() => {
    window.addEventListener('pagehide', leaveFunc);
    return () => {
      leaveRoom(true);
      window.removeEventListener('pagehide', leaveFunc);
    };
  }, [leaveRoom]);

  const clearIdleTimers = useCallback(() => {
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
      idleTimeoutRef.current = null;
    }
    if (idleCountdownRef.current) {
      clearInterval(idleCountdownRef.current);
      idleCountdownRef.current = null;
    }
  }, []);

  const handleContinueMeeting = useCallback(() => {
    clearIdleTimers();
    setIdlePromptOpen(false);
  }, [clearIdleTimers]);

  useEffect(() => {
    const intervalMs = config.idlePromptIntervalMs;
    const timeoutMs = config.idlePromptTimeoutMs;

    idleIntervalRef.current = setInterval(() => {
      setIdleSecondsLeft(Math.ceil(timeoutMs / 1000));
      setIdlePromptOpen(true);

      idleTimeoutRef.current = setTimeout(() => {
        clearIdleTimers();
        setIdlePromptOpen(false);
        message.warning('No response detected. Ending meeting.');
        leaveRoom(false);
      }, timeoutMs);

      idleCountdownRef.current = setInterval(() => {
        setIdleSecondsLeft((s) => (s > 0 ? s - 1 : 0));
      }, 1000);
    }, intervalMs);

    return () => {
      if (idleIntervalRef.current) {
        clearInterval(idleIntervalRef.current);
        idleIntervalRef.current = null;
      }
      clearIdleTimers();
    };
  }, [leaveRoom, clearIdleTimers]);

  const handleUserPublishStream = useCallback(
    (stream: { userId: string; mediaType: MediaType }) => {
      const userId = stream.userId;
      if (stream.mediaType & MediaType.VIDEO) {
        rtc.current?.setRemoteVideoPlayer(userId, `remoteStream_${userId}`);
      }
    },
    []
  );

  /**
   * @brief remove stream & update remote streams list
   * @param {Event} event
   */
  const handleUserUnpublishStream = (event: { userId: string; mediaType: MediaType }) => {
    const { userId, mediaType } = event;

    if (mediaType & MediaType.VIDEO) {
      rtc.current?.setRemoteVideoPlayer(userId, undefined);
    }
  };

  const handleUserStartVideoCapture = (event: { userId: string }) => {
    const { userId } = event;

    if (remoteStreams[userId]) {
      rtc.current?.setRemoteVideoPlayer(userId, `remoteStream_${userId}`);
    }
  };

  /**
   * Remove the user specified from the room in the local and clear the unused dom
   * @param {*} event
   */
  const handleUserStopVideoCapture = (event: { userId: string }) => {
    const { userId } = event;

    rtc.current?.setRemoteVideoPlayer(userId, undefined);
  };

  const handleUserJoin = (e: onUserJoinedEvent) => {
    console.log('handleUserJoin', e);

    const { userInfo } = e;
    const remoteUserId = userInfo.userId;

    if (Object.keys(remoteStreams).length < 3) {
      if (remoteStreams[remoteUserId]) return;
      remoteStreams[remoteUserId] = {
        playerComp: (
          <MediaPlayer
            userId={remoteUserId}
            setRemoteVideoPlayer={rtc.current?.setRemoteVideoPlayer}
          />
        ),
      };

      setRemoteStreams({
        ...remoteStreams,
      });
    }
  };

  useEffect(() => {
    const streams = Object.keys(remoteStreams);
    const _autoPlayFailUser = autoPlayFailUser.filter(
      (item) => streams.findIndex((stream) => stream === item) !== -1
    );
    setAutoPlayFailUser([..._autoPlayFailUser]);
  }, [remoteStreams]);

  const handleUserLeave = (e: onUserLeaveEvent) => {
    const { userInfo } = e;
    const remoteUserId = userInfo.userId;
    if (remoteStreams[remoteUserId]) {
      delete remoteStreams[remoteUserId];
    }
    setRemoteStreams({
      ...remoteStreams,
    });
  };

  useEffect(() => {
    (async () => {
      if (!roomId || !userId || !rtc.current) return;

      let token: string | null = null;

      const staticToken = config.tokens.find((item) => item.userId === userId);
      if (staticToken) {
        token = staticToken.token;
      } else if (config.tokenEndpoint) {
        try {
          const res = await fetch(config.tokenEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ roomId, userId }),
          });
          if (!res.ok) {
            const errBody = await res.text();
            throw new Error(`Token endpoint ${res.status}: ${errBody}`);
          }
          const data = (await res.json()) as { token?: string; error?: string; message?: string };
          if (!data.token) {
            throw new Error(data.message || data.error || 'Token endpoint returned no token');
          }
          token = data.token;
        } catch (err) {
          console.error('Failed to fetch RTC token', err);
          leaveRoom(false);
          setJoinFailReason(`Failed to fetch token: ${err instanceof Error ? err.message : String(err)}`);
          return;
        }
      }

      rtc.current
        .join((token as any) || null, roomId, userId)
        .then(() =>
          rtc?.current?.createLocalStream(userId, (res: any) => {
            const { code, msg, devicesStatus } = res;
            if (code === -1) {
              alert(msg);
              setMicOn(false);
              setVideoOn(false);
            }
          })
        )
        .catch((err: any) => {
          console.log('err', err);
          leaveRoom(false);
          setJoinFailReason(JSON.stringify(err));
        });
    })();
  }, [roomId, userId, rtc]);

  const changeMicState = useCallback((): void => {
    if (!rtc.current) return;
    rtc.current.changeAudioState(!isMicOn);
    setMicOn(!isMicOn);
  }, [isMicOn, rtc]);

  const changeVideoState = useCallback((): void => {
    if (!rtc.current) return;
    rtc.current.changeVideoState(!isVideoOn);
    setVideoOn(!isVideoOn);
  }, [isVideoOn, rtc]);

  const handleEventError = (e: any, VERTC: any) => {
    if (e.errorCode === VERTC.ErrorCode.DUPLICATE_LOGIN) {
      message.error('你的账号被其他人顶下线了');
      leaveRoom(false);
    }
  };

  const handleAutoPlayFail = (event: AutoPlayFailedEvent) => {
    console.log('handleAutoPlayFail', event.userId, event);
    const { userId, kind } = event;

    let playUser = playStatus.current?.[userId] || {};
    playUser = { ...playUser, [kind]: false };
    playStatus.current[userId] = playUser;

    addFailUser(userId);
  };

  const addFailUser = (userId: string) => {
    const index = autoPlayFailUser.findIndex((item) => item === userId);
    if (index === -1) {
      autoPlayFailUser.push(userId);
    }
    setAutoPlayFailUser([...autoPlayFailUser]);
  };

  const playerFail = (params: { type: 'audio' | 'video'; userId: string }) => {
    const { type, userId } = params;
    let playUser = playStatus.current?.[userId] || {};

    console.log('pause', params);

    playUser = { ...playUser, [type]: false };

    const { audio, video } = playUser;

    if (audio === false || video === false) {
      addFailUser(userId);
    }
  };

  const handlePlayerEvent = (event: PlayerEvent) => {
    const { userId, rawEvent, type } = event;

    console.log('handlePlayerEvent', event, userId, type, rawEvent.type);

    let playUser = playStatus.current?.[userId] || {};

    if (!playStatus.current) return;

    if (rawEvent.type === 'playing') {
      playUser = { ...playUser, [type]: true };
      const { audio, video } = playUser;
      if (audio !== false && video !== false) {
        const _autoPlayFailUser = autoPlayFailUserdRef.current.filter((item) => item !== userId);
        setAutoPlayFailUser([..._autoPlayFailUser]);
      }
    } else if (rawEvent.type === 'pause') {
      playerFail({ userId, type });
    }

    playStatus.current[userId] = playUser;
  };

  const handleAutoPlay = () => {
    const users: string[] = autoPlayFailUser;
    console.log('handleAutoPlay autoPlayFailUser', autoPlayFailUser);
    if (users && users.length) {
      users.forEach((user) => {
        rtc.current?.engine.play(user);
      });
    }
    setAutoPlayFailUser([]);
  };

  useEffect(() => {
    autoPlayFailUserdRef.current = autoPlayFailUser;
  }, [autoPlayFailUser]);

  const remoteCount = Object.keys(remoteStreams).length;

  return (
    <>
      <RTCComponent
        onRef={(ref: any) => (rtc.current = ref)}
        config={{
          ...config,
          roomId,
        }}
        streamOptions={streamOptions}
        handleUserPublishStream={handleUserPublishStream}
        handleUserUnpublishStream={handleUserUnpublishStream}
        handleUserStartVideoCapture={handleUserStartVideoCapture}
        handleUserStopVideoCapture={handleUserStopVideoCapture}
        handleUserJoin={handleUserJoin}
        handleUserLeave={handleUserLeave}
        handleEventError={handleEventError}
        handleAutoPlayFail={handleAutoPlayFail}
        handlePlayerEvent={handlePlayerEvent}
      />
      <Container ref={containerRef}>
        {remoteCount > 0 && (
          <RemoteGrid participantCount={remoteCount}>
            {Object.keys(remoteStreams)?.map((key) => {
              const Comp = remoteStreams[key].playerComp;
              return <RemoteTile key={key}>{Comp}</RemoteTile>;
            })}
          </RemoteGrid>
        )}
        <LocalContainer
          mode={remoteCount === 0 ? 'full' : 'pip'}
          corner={pipCorner}
          size={pipSize}
          isDragging={isDragging}
          dragPosition={dragPosition}
          onPointerDown={remoteCount > 0 ? handlePipPointerDown : undefined}
          onPointerMove={remoteCount > 0 ? handlePipPointerMove : undefined}
          onPointerUp={remoteCount > 0 ? handlePipPointerUp : undefined}
        >
          <div
            style={{
              width: '100%',
              height: '100%',
              position: 'relative',
              background: '#000',
            }}
            id={'local-player'}
          >
            <span
              style={{
                color: 'rgba(255, 255, 255, 0.9)',
                position: 'absolute',
                bottom: remoteCount === 0 ? '12px' : '6px',
                left: remoteCount === 0 ? '16px' : '8px',
                zIndex: 1000,
                background: 'rgba(0, 0, 0, 0.45)',
                backdropFilter: 'blur(10px)',
                padding: remoteCount === 0 ? '4px 10px' : '3px 8px',
                borderRadius: '999px',
                fontSize: remoteCount === 0 ? '12px' : '10px',
                fontWeight: 500,
                maxWidth: remoteCount === 0 ? '180px' : '100px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {userId}
            </span>
            {remoteCount > 0 && (
              <button
                type="button"
                aria-label={`Resize self video (current: ${SIZE_LABEL[pipSize]})`}
                title={`Size: ${SIZE_LABEL[pipSize]} — click to cycle`}
                onPointerDown={(ev) => ev.stopPropagation()}
                onClick={(ev) => {
                  ev.stopPropagation();
                  setPipSize((prev) => SIZE_CYCLE[prev]);
                }}
                style={{
                  position: 'absolute',
                  top: '6px',
                  right: '6px',
                  zIndex: 1001,
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'none',
                  borderRadius: '999px',
                  background: 'rgba(0, 0, 0, 0.55)',
                  backdropFilter: 'blur(10px)',
                  color: 'rgba(255, 255, 255, 0.95)',
                  fontSize: '11px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  padding: 0,
                  lineHeight: 1,
                }}
              >
                {SIZE_LABEL[pipSize]}
              </button>
            )}
          </div>
        </LocalContainer>
        {remoteCount === 0 && (
          <LocalOnlyOverlay>
            <WaitingCaption>Waiting for others to join...</WaitingCaption>
          </LocalOnlyOverlay>
        )}
      </Container>
      <ControlBar
        RClient={rtc}
        systemConf={[
          {
            moduleName: 'DividerModule',
            moduleProps: {
              width: 2,
              height: 32,
              marginL: 20,
            },
            visible: true,
          },
          {
            moduleName: 'HangUpModule',
            moduleProps: {
              changeHooks: () => {
                leaveRoom(false);
              },
            },
          },
        ]}
        moduleConf={[
          {
            moduleName: 'MicoPhoneControlModule',
            moduleProps: {
              changeHooks: () => changeMicState(),
              isMicOn,
            },
            visible: true,
          },
          {
            moduleName: 'VideoControlModule',
            moduleProps: {
              changeHooks: () => changeVideoState(),
              isVideoOn,
            },
            visible: true,
          },
        ]}
      />
      <AutoPlayModal handleAutoPlay={handleAutoPlay} autoPlayFailUser={autoPlayFailUser} />
      <Modal
        title="Are you still there?"
        visible={idlePromptOpen}
        closable={false}
        maskClosable={false}
        keyboard={false}
        okText={`Continue meeting (${idleSecondsLeft}s)`}
        cancelButtonProps={{ style: { display: 'none' } }}
        onOk={handleContinueMeeting}
      >
        <p>
          The meeting will end automatically in <strong>{idleSecondsLeft}s</strong> if you don&apos;t
          respond.
        </p>
      </Modal>
    </>
  );
};

export default Meeting;
